// src/pages/Home/TensorAICommand.js
// TensorPilot — Unified Landing Page
// One story: Individual discovery → Team adoption → Enterprise transformation

import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faLock, faGlobe,
  faDownload, faArrowRight, faCheck, faTimes,
  faMicrophone, faVideo, faImage, faCode, faFileAlt,
  faChevronDown, faChevronUp,
  faCalendarAlt, faBalanceScale, faBriefcase
} from '@fortawesome/free-solid-svg-icons';

import NavBarMain from '../../sharedcomponents/navbar/Navbar';
import ProductSchema from '../../sharedcomponents/ProductSchema';
import OrganizationSchema from '../../sharedcomponents/OrganizationSchema';
import SiteNavigationSchema from '../../sharedcomponents/SiteNavigationSchema';
import SocialProfileSchema from '../../sharedcomponents/SocialProfileSchema';

import CallToActionSection from './CommonSections/CallToActionSection';
import DownloadCallToActionSection from './CommonSections/DownloadCallToActionSection';
import InteractiveAppTour from './TensorPilotHomeSection/InteractiveAppTour';

import './css/TensorAICommand.css';

export const PROVIDERS = [
  {
    name: 'Google Gemini', key: 'google_gemini',
    logo: 'https://firebasestorage.googleapis.com/v0/b/aidronesoftwareprod.appspot.com/o/Blog%2FProviderImagesApp%2FGemini_logo.png?alt=media',
    models: ['Gemini 3.0 Pro', 'Gemini 2.5 Flash', 'Imagen 4', 'Veo 3.1'],
    color: '#4285F4',
    capabilities: ['Live Audio', 'Video Gen', 'Image Gen', '1M Context'],
    privacyPolicyUrl: 'https://policies.google.com/privacy',
    dataUsagePolicyUrl: 'https://ai.google.dev/gemini-api/terms',
    apiKeyUrl: 'https://aistudio.google.com/app/apikey'
  },
  {
    name: 'OpenAI', key: 'openai',
    logo: 'https://firebasestorage.googleapis.com/v0/b/aidronesoftwareprod.appspot.com/o/Blog%2FProviderImagesApp%2FOpenAI_logo.png?alt=media',
    models: ['GPT-5', 'o3 Pro', 'DALL-E 3', 'Sora 2'],
    color: '#10A37F',
    capabilities: ['Reasoning', 'Video Gen', 'Image Gen', 'Code'],
    privacyPolicyUrl: 'https://openai.com/enterprise-privacy/',
    dataUsagePolicyUrl: 'https://platform.openai.com/docs/guides/your-data',
    apiKeyUrl: 'https://platform.openai.com/api-keys'
  },
  {
    name: 'Anthropic Claude', key: 'anthropic_claude',
    logo: 'https://firebasestorage.googleapis.com/v0/b/aidronesoftwareprod.appspot.com/o/Blog%2FProviderImagesApp%2FAnthropic_logo.png?alt=media',
    models: ['Claude Opus 4.5', 'Claude Sonnet 4.5', 'Claude Haiku 4.5'],
    color: '#D97706',
    capabilities: ['Extended Thinking', '200K Context', 'Code Analysis'],
    privacyPolicyUrl: 'https://privacy.anthropic.com/en/',
    dataUsagePolicyUrl: 'https://privacy.anthropic.com/en/articles/7996885-how-do-you-use-personal-data-in-model-training',
    apiKeyUrl: 'https://console.anthropic.com/settings/keys'
  },
  {
    name: 'Grok', key: 'grok',
    logo: 'https://firebasestorage.googleapis.com/v0/b/aidronesoftwareprod.appspot.com/o/Blog%2FProviderImagesApp%2FGrok_logo.png?alt=media',
    models: ['Grok 4', 'Grok Code Fast', 'Grok Vision'],
    color: '#1DA1F2',
    capabilities: ['Real-time X Data', '2M Context', 'Image Gen'],
    privacyPolicyUrl: 'https://x.ai/legal/terms-of-service-enterprise',
    dataUsagePolicyUrl: 'https://x.ai/legal/terms-of-service-enterprise',
    apiKeyUrl: 'https://console.x.ai/'
  },
  {
    name: 'Perplexity', key: 'perplexity',
    logo: 'https://firebasestorage.googleapis.com/v0/b/aidronesoftwareprod.appspot.com/o/Blog%2FProviderImagesApp%2FPerplexity_logo.png?alt=media',
    models: ['Sonar Pro', 'Sonar Reasoning', 'Deep Research'],
    color: '#6366F1',
    capabilities: ['Live Web Search', 'Academic Research', 'Citations'],
    privacyPolicyUrl: 'https://www.perplexity.ai/privacy',
    dataUsagePolicyUrl: 'https://www.perplexity.ai/terms',
    apiKeyUrl: 'https://www.perplexity.ai/settings/api'
  },
  {
    name: 'Cohere', key: 'cohere',
    logo: 'https://firebasestorage.googleapis.com/v0/b/aidronesoftwareprod.appspot.com/o/Blog%2FProviderImagesApp%2FCohere_logo.png?alt=media',
    models: ['Command A', 'Command R+', 'Command R'],
    color: '#7B2BF9',
    capabilities: ['RAG', 'Tool Use', '256K Context'],
    privacyPolicyUrl: 'https://cohere.com/privacy',
    dataUsagePolicyUrl: 'https://cohere.com/terms-of-use',
    apiKeyUrl: 'https://dashboard.cohere.com/api-keys'
  },
  {
    name: 'Azure AI Foundry', key: 'azure_foundry',
    logo: 'https://firebasestorage.googleapis.com/v0/b/aidronesoftwareprod.appspot.com/o/Blog%2FProviderImagesApp%2FAzure_logo.png?alt=media',
    models: ['GPT-4o', 'GPT-5', 'o3', 'DeepSeek-V3'],
    color: '#0078D4',
    capabilities: ['Your Azure Tenant', 'Entra ID or API Key', 'Data Residency', 'OpenAI + Foundry'],
    privacyPolicyUrl: 'https://learn.microsoft.com/legal/cognitive-services/openai/data-privacy',
    dataUsagePolicyUrl: 'https://learn.microsoft.com/legal/cognitive-services/openai/data-privacy',
    apiKeyUrl: 'https://portal.azure.com'
  },
  {
    name: 'OpenRouter', key: 'openrouter',
    logo: 'https://openrouter.ai/icon-pwa.png',
    models: ['400+ active models', '60+ providers', '25+ free models', 'OpenAI, Anthropic, Google & more'],
    color: '#8B5CF6',
    capabilities: ['Single OpenRouter API key', 'Pay-as-you-go', 'Coding & agent workflows', 'Compare pricing live'],
    privacyPolicyUrl: 'https://openrouter.ai/privacy',
    dataUsagePolicyUrl: 'https://openrouter.ai/terms',
    apiKeyUrl: 'https://openrouter.ai/keys'
  }
];

const AUDIENCE_CARDS = [
  {
    title: 'Law & Professional Services',
    icon: faBalanceScale,
    points: [
      'Keep client matters on-device by default',
      'Track API spend per workspace for disbursements',
      'Route drafting vs. review to the right model'
    ]
  },
  {
    title: 'Consulting & Research Teams',
    icon: faBriefcase,
    points: [
      'Switch providers per engagement without tool sprawl',
      'Map token cost to client or project codes',
      'Use Working Set caching to cut repeat context spend'
    ]
  },
  {
    title: 'Regulated & Privacy-Led Orgs',
    icon: faShieldAlt,
    points: [
      'Direct device → provider API path (no TensorPilot proxy)',
      'Keys in OS keychain; optional Pro E2EE backup only',
      'Architecture that compliance teams can actually audit'
    ]
  }
];

const TRUST_SIGNALS = [
  { title: 'Vector Institute FastLane', subtitle: 'Canadian AI ecosystem' },
  { title: 'Local-First by Default', subtitle: 'Data stays on device' },
  { title: 'Direct API Routing', subtitle: 'No proxy middlemen' },
  { title: 'Enterprise Deployment Ready', subtitle: 'Team key pools & silent install' }
];

const FAQS = [
  {
    question: "How does TensorPilot keep my data private?",
    answer: "Workspaces and conversations are stored locally in SQLite on your device. API keys use OS-native keychains (macOS Keychain, Windows Credential Manager). AI requests go directly from your device to provider APIs — TensorPilot servers never see your prompts or responses. Optional Pro cloud backup encrypts everything on-device before upload; we cannot decrypt it."
  },
  {
    question: "How much can my organization actually save?",
    answer: "It depends on usage patterns, but typical organizations save 40–75% vs per-seat subscriptions. The key savings come from: (1) eliminating inactive seat costs — you pay only for API tokens actually used, (2) choosing the most cost-effective model per task, and (3) eliminating vendor lock-in markups. A 50-person team paying $49/user/month can typically reduce AI costs from ~$29,400/year to ~$7,200/year with BYOK APIs."
  },
  {
    question: "What enterprise key management options are available?",
    answer: "Three deployment models: Individual BYOK (each user manages their own keys), Team Key Pools (IT-distributed shared keys for centralized billing), and Centralized Key Vault Integration (HashiCorp Vault or AWS Secrets Manager)."
  },
  {
    question: "Can I track AI costs per client, project, or department?",
    answer: "Yes. The Usage Dashboard provides token-level cost tracking per workspace/project. Law firms and consultancies can accurately allocate AI costs as disbursements. Export detailed usage reports for client billing with full audit trails."
  },
  {
    question: "How should we assess HIPAA, GDPR, and similar frameworks?",
    answer: "By default, data stays on the device. Optional Pro backup stores only ciphertext on infrastructure we operate. Compliance depends on your workflows, provider BAAs, and whether you enable cloud backup — work with your compliance team and counsel."
  },
  {
    question: "How does the Knowledge Base reduce API costs?",
    answer: "Our two-level semantic search architecture reduces API calls by 60–90%. Local routing identifies relevant indexed content in milliseconds, then only queries the necessary folders. This means fewer tokens sent to AI providers — directly lowering your costs."
  },
  {
    question: "How does Working Set caching save on token costs?",
    answer: "Working Set lets you pin files as persistent context across messages. TensorPilot tracks file hashes — unchanged files are marked as cached and only new or modified content is re-sent. This means if you pin 8 files but only edit 1, you pay tokens for that 1 file, not all 8. Typical savings: 70–85% fewer tokens per message when working on a stable codebase."
  },
  {
    question: "Can TensorPilot be rolled out organization-wide?",
    answer: "Yes. Enterprise installers for all platforms (macOS, Windows, Linux). Configuration profiles can pre-populate team API keys and workspace settings. Silent installation is supported."
  },
  {
    question: "What happens to our data if we stop using TensorPilot?",
    answer: "Since all data is stored locally on your devices, you retain complete ownership. Uninstalling removes TensorPilot but your documents and files remain untouched. No vendor lock-in, no data hostage."
  }
];

const CAPABILITIES = [
  { icon: faMicrophone, label: 'Live Audio', description: 'Real-time voice conversations with AI' },
  { icon: faVideo, label: 'Video Analysis', description: 'Native video processing & generation' },
  { icon: faImage, label: 'Image Generation', description: 'DALL-E 3, Imagen 4, Grok Image' },
  { icon: faCode, label: 'Code Intelligence', description: 'Full codebase context & analysis' },
  { icon: faFileAlt, label: 'Document Processing', description: 'PDF, DOCX, XLSX, PPTX support' },
  { icon: faGlobe, label: 'Web Search', description: 'Real-time data via Perplexity' }
];

// ──────────────────────────────────────────────────────────────────────
// FAQ Accordion
// ──────────────────────────────────────────────────────────────────────
const FAQAccordion = ({ faqs, initialVisible = 5 }) => {
  const [openItems, setOpenItems] = useState({});
  const [showAll, setShowAll] = useState(false);

  const toggleItem = (index) => {
    setOpenItems(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const visibleFaqs = showAll ? faqs : faqs.slice(0, initialVisible);
  const hasMore = faqs.length > initialVisible;

  return (
    <div className="tp-faq-accordion">
      {visibleFaqs.map((faq, i) => {
        const idx = faqs.indexOf(faq);
        return (
          <div key={faq.question} className={`faq-accordion-item ${openItems[idx] ? 'open' : ''}`}>
            <button className="faq-accordion-header" onClick={() => toggleItem(idx)} aria-expanded={openItems[idx]}>
              <span className="faq-question">{faq.question}</span>
              <FontAwesomeIcon icon={openItems[idx] ? faChevronUp : faChevronDown} className="faq-toggle-icon" />
            </button>
            <div className={`faq-accordion-body ${openItems[idx] ? 'expanded' : ''}`}>
              <div className="faq-answer">{faq.answer}</div>
            </div>
          </div>
        );
      })}
      {hasMore && !showAll && (
        <button className="faq-show-more-btn" onClick={() => setShowAll(true)}>
          Show More ({faqs.length - initialVisible} more)
          <FontAwesomeIcon icon={faChevronDown} className="show-more-icon" />
        </button>
      )}
      {showAll && hasMore && (
        <button className="faq-show-more-btn" onClick={() => setShowAll(false)}>
          Show Less
          <FontAwesomeIcon icon={faChevronUp} className="show-more-icon" />
        </button>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Inline Download CTA — strategic placement after high-intent sections
// variant: 'on-white' or 'on-gray' to match parent section background
// ──────────────────────────────────────────────────────────────────────
const InlineDownloadCTA = ({ context = 'default', variant = 'on-white' }) => {
  const messages = {
    'after-cases': {
      headline: 'Try the Desktop App Free',
      subtext: 'Windows, macOS, and Linux. Your API keys. No credit card required.'
    },
    'after-providers': {
      headline: 'Access All These Models in One App',
      subtext: 'Switch between providers instantly. Your keys, your data, your control.'
    },
    'after-trust': {
      headline: 'Experience Privacy-First AI',
      subtext: 'Download free and keep your data local. Optional Pro cloud backup available.'
    }
  };

  const msg = messages[context] || messages['after-cases'];

  return (
    <div className="tp-inline-download-cta">
      <Container>
        <div className={`tp-inline-cta-content tp-inline-cta-${variant}`}>
          <div className="tp-inline-cta-text">
            <h3>{msg.headline}</h3>
            <p>{msg.subtext}</p>
          </div>
          <div className="tp-inline-cta-actions">
            <Button as={Link} to="/download" className="tp-inline-cta-primary">
              <FontAwesomeIcon icon={faDownload} />
              Download Free
            </Button>
            <Link to="/sales" className="tp-inline-cta-link">
              See Pricing <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </div>
      </Container>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Main Page Component
// ──────────────────────────────────────────────────────────────────────
const TensorAICommand = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.substring(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>TensorPilot | The Sovereign AI Operating System</title>
        <meta name="description" content="All the AI power, none of the data risk. Desktop AI with BYOK—including OpenRouter's 400+ model catalog (60+ upstream providers) plus native APIs. Save 40-75% vs typical subscriptions. Canadian-built, privacy-first." />
        <meta name="keywords" content="Private AI, Desktop AI, BYOK AI, ChatGPT Alternative, Claude API, GPT-5 API, Grok API, Enterprise AI, Data Privacy, Local AI, Canadian AI, API Cost Savings, AI Cost Calculator" />
        <meta property="og:title" content="TensorPilot | The Sovereign AI Operating System" />
        <meta property="og:description" content="All the AI power, none of the data risk. Save 40-75% vs per-seat AI subscriptions. 400+ models via OpenRouter (60+ providers) plus direct APIs. Privacy-first. Canadian-built." />
        <meta property="og:url" content="https://www.tensorpilot.io/" />
        <meta property="og:image" content="https://www.tensorpilot.io/Logo.png" />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TensorPilot | The Sovereign AI Operating System" />
        <meta name="twitter:description" content="Desktop AI with direct provider APIs. Save 40-75%. Local-first. Not used for training." />
        <link rel="canonical" href="https://www.tensorpilot.io/" />
      </Helmet>

      <ProductSchema
        name="TensorPilot"
        description="The Sovereign AI Operating System. Desktop app with direct APIs plus OpenRouter's catalog—400+ models from 60+ providers. Privacy-first, local-first."
        image="https://www.tensorpilot.io/Logo.png"
        category="Software > AI > Desktop Application"
        price="0"
        priceCurrency="USD"
        availability="InStock"
        url="https://www.tensorpilot.io/"
        sku="tensor-pilot-command"
        brand="TensorPilot"
      />
      <OrganizationSchema />
      <SiteNavigationSchema />
      <SocialProfileSchema />

      <NavBarMain />

            {/* ═══════════════════════════════════════════════════════════════════
          HERO — Cursor Style
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-hero">
        <div className="tp-hero-backdrop" />
        <Container className="tp-hero-container">
          <div className="tp-hero-text-center">
            <div className="tp-hero-badge-row justify-content-center">
              <span className="tp-hero-badge tp-hero-badge-alt">
                <FontAwesomeIcon icon={faShieldAlt} className="badge-icon" />
                Privacy-First
              </span>
            </div>

            <h1 className="tp-hero-headline">
              <span className="tp-headline-line1">All the AI Power.</span>
              <span className="tp-headline-line2">None of the Data Risk.</span>
            </h1>

            <p className="tp-hero-sub mx-auto">
              The desktop AI workspace that routes directly to your providers — including 400+ models through OpenRouter (60+ upstream providers; 25+ with free tiers) plus native APIs for Gemini, OpenAI, Claude, Grok, Perplexity, and Cohere. Your keys, your device, your data.
            </p>

            <div className="tp-hero-ctas justify-content-center">
              <Button className="tp-cta-primary" as={Link} to="/download">
                <FontAwesomeIcon icon={faDownload} className="cta-icon" />
                Download TensorPilot
              </Button>
              <Button
                className="tp-cta-secondary"
                as={Link}
                to="/demo"
              >
                <FontAwesomeIcon icon={faCalendarAlt} className="cta-icon" />
                Book a Demo
              </Button>
              <Link to="/sales" className="tp-cta-tertiary">
                See Pricing <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </div>
          </div>

          {/* Interactive product tour */}
          <InteractiveAppTour providers={PROVIDERS} />

          <div className="tp-trust-strip" aria-label="Trust signals">
            {TRUST_SIGNALS.map((signal) => (
              <div key={signal.title} className="tp-trust-pill">
                <span className="tp-trust-dot" />
                <div className="tp-trust-text">
                  <strong>{signal.title}</strong>
                  <span>{signal.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          BENTO BOX — Visual Features & Solutions
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-bento-section" id="features">
        <Container>
          <div className="tp-section-header">
            <span className="tp-section-eyebrow">The TensorPilot Advantage</span>
            <h2 className="tp-section-title">
              Power. Privacy. <span className="tp-accent">Zero Compromise.</span>
            </h2>
          </div>

          <div className="tp-bento-grid">
            {/* Bento Item 1: Direct API / Local First (Large) */}
            <div className="tp-bento-card tp-bento-large tp-bento-local">
              <div className="tp-bento-content">
                <h3>Direct API. 100% Local.</h3>
                <p>No proxy servers. No data harvesting. Your keys, your device.</p>
              </div>
              <div className="tp-bento-visual">
                <div className="tp-local-viz">
                  <div className="tp-viz-node user-node">
                    <FontAwesomeIcon icon={faLock} />
                    <span>Your Device</span>
                  </div>
                  <div className="tp-viz-connection">
                    <div className="tp-viz-line"></div>
                    <span className="tp-viz-badge">Encrypted API Call</span>
                  </div>
                  <div className="tp-viz-node ai-node">
                    <FontAwesomeIcon icon={faGlobe} />
                    <span>AI Provider</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bento Item 2: Cost Savings (Medium) */}
            <div className="tp-bento-card tp-bento-cost">
              <div className="tp-bento-content">
                <h3>Slash Costs by 75%</h3>
                <p>Pay for tokens used, not idle seats.</p>
              </div>
              <div className="tp-bento-visual">
                <div className="tp-cost-viz">
                  <div className="tp-bar-group">
                    <div className="tp-bar-label">SaaS Seat</div>
                    <div className="tp-bar-value">$360/yr</div>
                    <div className="tp-bar-track">
                      <div className="tp-bar tp-bar-high" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="tp-bar-group tp-bar-group--low">
                    <div className="tp-bar-label">TensorPilot (BYOK)</div>
                    <div className="tp-bar-value">~$15/yr</div>
                    <div className="tp-bar-track">
                      <div className="tp-bar tp-bar-low" aria-hidden="true" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bento Item 3: Model breadth (Medium) */}
            <div className="tp-bento-card tp-bento-models">
              <div className="tp-bento-content">
                <h3>400+ Models. One App.</h3>
                <p>OpenRouter's unified catalog plus direct APIs — Claude, GPT, Gemini, DeepSeek, Mistral, and more.</p>
              </div>
              <div className="tp-bento-visual">
                <div className="tp-models-viz">
                  {PROVIDERS.map((p, i) => (
                    <div key={i} className="tp-model-icon-wrap" style={{ '--m-color': p.color }}>
                      <img src={p.logo} alt={p.name} className={p.logoClass || ''} />
                    </div>
                  ))}
                  <div className="tp-model-icon-wrap more">
                    <span>400+</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bento Item 4: Multimodal (Small) */}
            <div className="tp-bento-card tp-bento-small tp-bento-multi">
              <div className="tp-bento-content">
                <h3>Multimodal Native</h3>
                <p>Voice, vision, documents, and code — all in one workspace.</p>
              </div>
              <div className="tp-bento-visual">
                <div className="tp-multi-icons">
                  {[
                    { icon: faMicrophone, label: 'Voice', bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.3)' },
                    { icon: faImage, label: 'Vision', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)' },
                    { icon: faCode, label: 'Code', bg: 'rgba(37,99,235,0.12)', border: 'rgba(37,99,235,0.3)' },
                    { icon: faFileAlt, label: 'Docs', bg: 'rgba(217,119,6,0.12)', border: 'rgba(217,119,6,0.3)' },
                  ].map((m) => (
                    <div key={m.label} className="tp-multi-icon-item">
                      <div className="tp-multi-icon-circle" style={{ background: m.bg, border: `1.5px solid ${m.border}` }}>
                        <FontAwesomeIcon icon={m.icon} style={{ color: m.border.replace('0.3', '1'), fontSize: '22px' }} />
                      </div>
                      <label>{m.label}</label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bento Item 5: Enterprise Ready (Small) */}
            <div className="tp-bento-card tp-bento-small tp-bento-enterprise">
              <div className="tp-bento-content">
                <h3>Enterprise Ready</h3>
              </div>
              <div className="tp-bento-visual">
                <div className="tp-ent-badges">
                  <span>BYOK</span>
                  <span>Vault</span>
                  <span>Usage</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          RECOGNITION + WHO IT'S FOR (honest — no fabricated quotes)
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-cases-section" id="case-studies">
        <Container>
          <div className="tp-section-header">
            <span className="tp-section-eyebrow">Recognition</span>
            <h2 className="tp-section-title">
              Built in Canada. <span className="tp-accent">Backed by the right ecosystem.</span>
            </h2>
            <p className="tp-section-subtitle">
              TensorPilot is part of the Vector Institute FastLane program — Canada&apos;s leading AI commercialization network for startups building serious AI products.
            </p>
          </div>

          <div className="tp-recognition-banner">
            <a
              className="tp-recognition-badge"
              href="https://vectorinstitute.ai/ai-adoption/startups/fastlane/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Vector Institute FastLane — Active Member"
            >
              <img
                src="/fastlane-badge.png"
                alt="Vector Institute FastLane Active Member badge"
                loading="lazy"
              />
            </a>
            <div className="tp-recognition-copy">
              <strong>Vector Institute FastLane — Active Member</strong>
              <span>TensorPilot is an active member of Vector&apos;s FastLane program, supporting Canadian AI startups with research-backed commercialization pathways.</span>
              <a
                className="tp-recognition-link"
                href="https://vectorinstitute.ai/ai-adoption/startups/fastlane/"
                target="_blank"
                rel="noopener noreferrer"
              >
                About FastLane <FontAwesomeIcon icon={faArrowRight} />
              </a>
            </div>
          </div>

          <div className="tp-section-header tp-audience-header">
            <span className="tp-section-eyebrow">Who it&apos;s for</span>
            <h2 className="tp-section-title">
              Designed for teams that can&apos;t treat AI as a <span className="tp-accent">black box.</span>
            </h2>
          </div>

          <div className="tp-cases-grid">
            {AUDIENCE_CARDS.map((card) => (
              <div key={card.title} className="tp-case-card tp-audience-card">
                <div className="tp-case-header">
                  <span className="tp-case-icon"><FontAwesomeIcon icon={card.icon} /></span>
                  <div>
                    <h3 className="tp-case-company">{card.title}</h3>
                  </div>
                </div>
                <ul className="tp-audience-points">
                  {card.points.map((point) => (
                    <li key={point}>
                      <FontAwesomeIcon icon={faCheck} className="tp-audience-check" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <InlineDownloadCTA context="after-cases" variant="on-white" />

      {/* ═══════════════════════════════════════════════════════════════════
          PROVIDERS — 400+ Models
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-providers-section" id="providers">
        <Container>
          <div className="tp-section-header">
            <span className="tp-section-eyebrow">Model Marketplace</span>
            <h2 className="tp-section-title">
              <span className="tp-accent">400+ Models</span> via OpenRouter — Direct APIs Too
            </h2>
            <p className="tp-section-subtitle">
              OpenRouter exposes 400+ pay-as-you-go models from 60+ upstream providers behind one API key (25+ models offer free tiers). TensorPilot adds native connections when you prefer direct keys—switch models per conversation.
            </p>
            <p className="tp-section-subtitle tp-providers-stack-note">
              Model gateways like OpenRouter are built for integrations and listing live pricing at{' '}
              <a href="https://openrouter.ai/models" target="_blank" rel="noopener noreferrer">openrouter.ai/models</a>.
              TensorPilot is the deployable desktop layer for teams—local workspace,
              compliance-friendly architecture, and fleet rollout without custom engineering.
            </p>
          </div>

          <div className="tp-providers-carousel-wrap">
            <div className="tp-providers-carousel">
              {PROVIDERS.map((p) => (
                <div key={p.key} className="tp-provider-card" style={{ '--prov-accent': p.color }}>
                  <div className="tp-provider-logo-wrap">
                    {p.logo && (
                      <img src={p.logo} alt={`${p.name} logo`} className={`tp-provider-logo ${p.logoClass || ''}`} />
                    )}
                  </div>
                  <h4 className="tp-provider-name">{p.name}</h4>
                  <div className="tp-provider-models">
                    {p.models.slice(0, 3).map((m, mi) => (
                      <span key={mi} className="tp-model-tag">{m}</span>
                    ))}
                  </div>
                  <div className="tp-provider-caps">
                    {p.capabilities.slice(0, 2).map((c, ci) => (
                      <span key={ci} className="tp-cap-badge">
                        <FontAwesomeIcon icon={faCheck} /> {c}
                      </span>
                    ))}
                  </div>
                  <div className="tp-provider-links">
                    {p.apiKeyUrl && (
                      <a href={p.apiKeyUrl} target="_blank" rel="noopener noreferrer" className="tp-prov-link tp-prov-link-key">
                        Get API Key
                      </a>
                    )}
                    {p.privacyPolicyUrl && (
                      <a href={p.privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="tp-prov-link">
                        Privacy
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="tp-providers-cta">
            <Link to="/ai-models" className="tp-text-link">
              Explore model catalog <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </Container>
      </section>

      <InlineDownloadCTA context="after-providers" variant="on-gray" />

      {/* ═══════════════════════════════════════════════════════════════════
          CAPABILITIES — Multimodal
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-capabilities-section" id="capabilities">
        <Container>
          <div className="tp-section-header">
            <span className="tp-section-eyebrow">Capabilities</span>
            <h2 className="tp-section-title">
              <span className="tp-accent">Multimodal</span> Intelligence
            </h2>
            <p className="tp-section-subtitle">
              Text, audio, video, images, documents — all processed locally with your API keys
            </p>
          </div>
          <div className="tp-capabilities-grid">
            {CAPABILITIES.map((cap, idx) => (
              <div key={idx} className="tp-cap-card">
                <div className="tp-cap-icon-wrap">
                  <FontAwesomeIcon icon={cap.icon} />
                </div>
                <h4>{cap.label}</h4>
                <p>{cap.description}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          TRUST — Compliance + Architecture
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-trust-section" id="trust">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <span className="tp-section-eyebrow">Trust & Compliance</span>
              <h2 className="tp-trust-title">
                <span className="tp-accent">Compliance-Ready</span> by Architecture
              </h2>
              <p className="tp-trust-description">
                Local-first architecture: AI requests go directly from your device to provider APIs using your own keys.
                Optional Pro cloud backup stores only client-encrypted data — we cannot read your chats.
              </p>
              <div className="tp-compliance-badges">
                {[
                  { name: 'GDPR', note: 'Data Stays in Region' },
                  { name: 'HIPAA', note: 'Assess w/ Provider BAAs' },
                  { name: 'CCPA', note: 'No Data Collection' },
                  { name: 'PIPEDA', note: 'Canadian Privacy Law' }
                ].map((c, i) => (
                  <div key={i} className="tp-compliance-item">
                    <FontAwesomeIcon icon={faCheck} className="tp-comp-check" />
                    <div>
                      <strong>{c.name}</strong>
                      <span>{c.note}</span>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/why-tensorpilot" className="tp-text-link tp-trust-explore-link">
                Why TensorPilot: Privacy & Cost Savings <FontAwesomeIcon icon={faArrowRight} />
              </Link>
            </Col>
            <Col lg={6}>
              <div className="tp-data-flow">
                <div className="tp-flow-header">Data Flow Guarantee</div>
                <div className="tp-flow-body">
                  <div className="tp-flow-node tp-flow-device">
                    <span className="tp-flow-emoji"><FontAwesomeIcon icon={faCode} /></span>
                    <strong>Your Device</strong>
                    <span>Conversations, Files, Keys</span>
                  </div>
                  <div className="tp-flow-arrow">
                    <span>Direct API →</span>
                  </div>
                  <div className="tp-flow-node tp-flow-provider">
                    <span className="tp-flow-emoji"><FontAwesomeIcon icon={faGlobe} /></span>
                    <strong>AI Provider</strong>
                    <span>Your Enterprise Agreement</span>
                  </div>
                </div>
                <div className="tp-flow-footer">
                  <FontAwesomeIcon icon={faTimes} className="me-2 text-danger" /> TensorPilot servers never see your data
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <InlineDownloadCTA context="after-trust" variant="on-gray" />

      {/* ═══════════════════════════════════════════════════════════════════
          DOWNLOAD
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-download-section" id="download-section">
        <Container>
          <DownloadCallToActionSection
            mainHeading="Download TensorPilot"
            subHeading="Free for individuals. Enterprise deployment options available."
            isEnterprise={true}
          />
        </Container>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FAQ
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="tp-faq-section" id="faq-section">
        <Container>
          <div className="tp-section-header">
            <h2 className="tp-section-title">
              Frequently Asked <span className="tp-accent">Questions</span>
            </h2>
            <p className="tp-section-subtitle">
              Questions from IT leaders, legal teams, and CFOs
            </p>
          </div>
          <FAQAccordion faqs={FAQS} initialVisible={5} />
        </Container>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          FINAL CTA
      ═══════════════════════════════════════════════════════════════════ */}
      <CallToActionSection isEnterprise={true} />
    </>
  );
};

export default TensorAICommand;
